import { flow, types, onSnapshot } from "mobx-state-tree";

import { AuthModel, UserModel } from "models/Auth";

import api, { assignAuth, assignOnRefresh } from "services/api";

type ResetPasswordPayload = {
  userId: number;
  code: string;
  newPassword: string;
};

const AuthStore = types
  .model({
    tokenData: types.maybe(AuthModel),
    user: types.maybe(UserModel),
    isLoading: types.maybe(types.boolean),
  })
  .views(self => ({
    get isAuthorized() {
      return !!self.tokenData?.accessToken;
    },

    get fullName() {
      return (
        [self.user?.firstName, self.user?.lastName].filter(Boolean).join(" ") ??
        ""
      );
    },
  }))
  .actions(self => ({
    logout: () => {
      window.localStorage.removeItem("tokenData");
      self.tokenData = undefined;
    },
  }))
  .actions(self => ({
    refreshToken: flow(function*() {
      try {
        self.isLoading = true;
        const result = yield api.post("/account/refresh", {
          token: Auth.tokenData?.refreshToken,
        });

        if (result.accessToken) {
          self.tokenData = result;
        }
        return result;
      } catch (error) {
        console.log("error :>> ", error);
        self.logout();
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
  }))
  .actions(self => ({
    afterCreate() {
      const tokenData = window.localStorage.getItem("tokenData");
      assignOnRefresh(self.refreshToken);
      if (tokenData) {
        const authObject = JSON.parse(tokenData);
        self.tokenData = authObject;
        assignAuth(authObject);
      }

      onSnapshot(self, ({ tokenData }) => {
        assignAuth(tokenData);
        assignOnRefresh(self.refreshToken);
        if (tokenData) {
          window.localStorage.setItem("tokenData", JSON.stringify(tokenData));
        }
      });
    },
    login: flow(function*(email: string, password: string) {
      try {
        self.isLoading = true;
        const result = yield api.post("/account/login", {
          email: email,
          password: password,
        });

        if (result.accessToken) {
          self.tokenData = result;
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    preChangePasswordValidate: flow(function*({
      userId,
      code,
    }: {
      userId: number;
      code: string;
    }) {
      try {
        return yield api.post(`/mobile/account/reset-password/validate`, {
          userId,
          code,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      }
    }),

    changePassword: flow(function*(payload: ResetPasswordPayload) {
      self.isLoading = true;

      try {
        return yield api.post("/mobile/account/reset-password", payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
  }));

const Auth = AuthStore.create();

export default Auth;
